import React from "react";
import {
  UseFormRegister,
  UseFormSetValue,
  FieldArrayWithId,
  FieldErrors,
} from "react-hook-form";
import { Plus } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Button } from "components/ui/button";
import { type CreateCampaignRouletteFormData } from "../../validations/campaign-roulette.validation";

interface ProbabilitiesCardProps {
  register: UseFormRegister<CreateCampaignRouletteFormData>;
  setValue: UseFormSetValue<CreateCampaignRouletteFormData>;
  probabilityFields: FieldArrayWithId<
    CreateCampaignRouletteFormData,
    "probabilities",
    "id"
  >[];
  appendProbability: (value: { prob: number; prizes: number[] }) => void;
  watchProbabilities: Array<{ prob: number; prizes: number[] }> | undefined;
  totalProbability: number;
  errors: FieldErrors<CreateCampaignRouletteFormData>;
  onResetProbabilities: () => void;
}

export const ProbabilitiesCard: React.FC<ProbabilitiesCardProps> = ({
  register,
  setValue,
  probabilityFields,
  appendProbability,
  watchProbabilities,
  totalProbability,
  errors,
  onResetProbabilities,
}) => {
  const isTotalValid = Math.abs(totalProbability - 100) < 0.01;
  const totalColor = isTotalValid ? "text-green-600" : "text-red-600";
  const canAddMore = probabilityFields.length < 6;
  const isExactlySix = probabilityFields.length === 6;

  const handlePrizesChange = (index: number, value: string) => {
    const values = value
      .split(",")
      .map((v) => parseFloat(v.trim()))
      .filter((v) => !isNaN(v));
    setValue(`probabilities.${index}.prizes`, values);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Probabilidades dos Prêmios</CardTitle>
        <div className="flex items-center gap-4">
          <span className={`text-sm font-medium ${totalColor}`}>
            Total: {totalProbability.toFixed(2)}%
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onResetProbabilities}
          >
            Resetar Padrão
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {probabilityFields.map((field, index) => (
          <div
            key={field.id}
            className="grid grid-cols-1 md:grid-cols-3 gap-6 p-5 border rounded-lg"
          >
            <div>
              <Label htmlFor={`probabilities.${index}.prob`}>
                Probabilidade (%)
              </Label>
              <Input
                id={`probabilities.${index}.prob`}
                type="number"
                step="0.01"
                min="0"
                max="100"
                onWheel={(e) => e.currentTarget.blur()}
                {...register(`probabilities.${index}.prob`, {
                  valueAsNumber: true,
                  setValueAs: (value) => {
                    if (
                      value === null ||
                      value === undefined ||
                      value === "" ||
                      isNaN(Number(value))
                    ) {
                      return 0;
                    }
                    return Number(value);
                  },
                })}
              />
              {errors.probabilities?.[index]?.prob && (
                <p className="text-sm text-red-500 mt-1">
                  {errors.probabilities[index]?.prob?.message}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor={`probabilities.${index}.prizes`}>
                Prêmios (R$)
              </Label>
              <Input
                id={`probabilities.${index}.prizes`}
                placeholder="Ex: 10,20,50"
                value={watchProbabilities?.[index]?.prizes?.join(",") || ""}
                onChange={(e) => handlePrizesChange(index, e.target.value)}
              />
              {errors.probabilities?.[index]?.prizes && (
                <p className="text-sm text-red-500 mt-1">
                  {errors.probabilities[index]?.prizes?.message}
                </p>
              )}
            </div>
          </div>
        ))}

        {canAddMore && (
          <Button
            type="button"
            variant="outline"
            onClick={() => appendProbability({ prob: 0, prizes: [] })}
          >
            <Plus className="h-4 w-4 mr-2" />
            Adicionar Probabilidade ({probabilityFields.length}/6)
          </Button>
        )}

        {!isExactlySix && (
          <p className="text-sm text-amber-600 mt-2 flex items-center gap-2">
            <span className="inline-block w-2 h-2 bg-amber-500 rounded-full"></span>
            É obrigatório ter exatamente 6 probabilidades. Atual:{" "}
            {probabilityFields.length}/6
          </p>
        )}
      </CardContent>
    </Card>
  );
};
